// src/lib/track-family.ts

/**
 * Version families — the client half of `/api/track-families`.
 *
 * WHY AN ENDPOINT AND NOT A PATCH. Until these routes existed the browser owned
 * `parent_track_id` and `is_published`: it wrote them over PostgREST under the member's own JWT,
 * and every rule about families ("exactly two levels, one label, both drafts") was a convention
 * the client agreed to keep. The rules now live in `services/track_family_service.py`. This module
 * knows the wire shape and nothing else — it never decides whether an attach is allowed, it asks.
 *
 * WHAT THE SELLER READS. Every refusal comes back as `{reason, message}`. `reason` is the contract
 * the UI branches on; `message` is the sentence the server wrote, with the counts already filled
 * in ("“Lantern” has 2 live versions…"). `REFUSAL_MESSAGES` mirrors those sentences for the one
 * case the server cannot speak in — a request that never arrived — so a failed fetch still names
 * the rule rather than printing a stack trace at a seller.
 *
 * IT NEVER WRITES TO THE DRAFTS STORE. `searchDraftMains` reads the same rows as the drafts list
 * and returns them instead of committing them: the picker must not replace the list behind the
 * sheet it was opened from.
 */

import { apiClient, ApiError } from "./api-client";
import { LabelCacheService } from "./labels";
import { toOrFilterSearchTerm } from "./search-ranking";
import { supabase } from "./supabase";
import { UserCacheService } from "./user-cache";
import type { Tables } from "@/types/database";

// ─────────────────────────────────────────────────────────────────────────────
// Vocabulary — mirrors `VERSION_TYPES` / `VERSION_LENGTHS` in the service
// ─────────────────────────────────────────────────────────────────────────────

/**
 * The offered version types. "Other — enter your own" is deliberately NOT one of them: it is the
 * dropdown's escape hatch, and whatever is typed behind it is stored as typed.
 */
export const VERSION_TYPES = [
  "Instrumental",
  "Underscore",
  "Vocal Lead",
  "Vocal Textures",
  "Vocal Choir",
  "Backing Vocals Only",
  "Solo",
  "Vocal Group",
  "Vocal Spoken Word",
  "Stem",
  "A cappella",
  "Radio Edit",
  "Clean",
  "Explicit",
  "Remix",
  "Acoustic",
  "Live",
  "Extended",
] as const;

/** The length. Optional, and a closed list: exactly the eight values the catalogue holds today. */
export const VERSION_LENGTHS = [
  "Full Length",
  "60 Second",
  "30 Second",
  "20 Second",
  "15 Second",
  "10 Second",
  "Ident",
  "Stab",
] as const;

/** Words that read as a placeholder rather than a type. Refused by the server; mirrored here. */
const RESERVED_VERSION_TYPES = new Set(["other", "main"]);

/** Limits the server enforces. Duplicated as numbers because the sentences below quote them. */
export const MAX_VERSION_TYPE_LENGTH = 60;
export const MAX_VERSIONS_PER_FAMILY = 40;
export const MAX_MAINS_PER_PUBLISH = 10;
/** How many draft mains the picker offers. */
export const PICKER_RESULT_LIMIT = 20;

/**
 * Is this one of the listed types, rather than free text someone typed behind "Other"?
 * Case-insensitive, because the server re-spells a listed type ("INSTRUMENTAL" → "Instrumental")
 * and a row written before it did so may still carry the other casing.
 */
export function isKnownVersionType(value: string | null | undefined): boolean {
  if (!value) return false;
  const lower = value.trim().toLowerCase();
  return VERSION_TYPES.some((type) => type.toLowerCase() === lower);
}

// ─────────────────────────────────────────────────────────────────────────────
// Refusals
// ─────────────────────────────────────────────────────────────────────────────

/** Which write a refusal came out of. Only `not_a_draft` reads differently per operation. */
export type TrackFamilyOperation =
  | "preview"
  | "attach"
  | "detach"
  | "publish"
  | "move_to_drafts"
  /** `POST /api/delete_tracks` — not one of these routes, but refused by the same rules. */
  | "delete";

export type TrackFamilyReason =
  | "forbidden"
  | "no_label"
  | "not_found"
  | "same_track"
  | "target_is_version"
  | "already_a_version"
  | "not_a_draft"
  | "attached_has_live_versions"
  | "type_required"
  | "type_too_long"
  | "type_reserved"
  | "bad_length"
  | "family_too_large"
  | "not_a_version"
  | "family_changed"
  | "analysing"
  | "analysis_failed"
  | "parent_no_tier"
  | "row_incomplete"
  | "embed_failed"
  | "publish_failed"
  | "publish_timeout"
  | "has_deals"
  | "attach_inconsistent"
  /** Not a server reason: the request never got an answer. */
  | "unknown";

/**
 * The sentence for each rule, mirrored from `services/track_family_service.py`.
 *
 * The server sends its own `message` and that one wins — it is the only one that can name a title
 * or a count. This map is what a seller reads when the request itself failed, and it is what the
 * test pins so the two clients of these rules cannot drift into wording them differently.
 */
export const REFUSAL_MESSAGES: Record<TrackFamilyReason, string> = {
  forbidden: "You don't have permission to change tracks.",
  no_label: "Your account isn't linked to a label.",
  not_found: "We couldn't find that track.",
  same_track: "A track can't be a version of itself.",
  target_is_version: "Pick a main track, not a version.",
  already_a_version:
    "This track is already a version of another track. Detach it first.",
  not_a_draft: "Move the track to drafts first, then try again.",
  // The server's own wording names the track and the count; this stands in when it cannot.
  attached_has_live_versions:
    "That track has live versions. Open it and move them to drafts first.",
  type_required: "Choose a version type.",
  type_too_long: `Keep the type under ${MAX_VERSION_TYPE_LENGTH} characters.`,
  type_reserved: "Enter the version type, for example “TV Mix”.",
  bad_length: "Choose a length from the list.",
  family_too_large: `A track can have at most ${MAX_VERSIONS_PER_FAMILY} versions.`,
  not_a_version: "This track is already a main track.",
  family_changed:
    "The versions of this track just changed. Check the new count and confirm again.",
  analysing: "Still analysing. You can publish when it finishes.",
  analysis_failed: "Analysis failed. Open the track and press Retry.",
  parent_no_tier: "Set a licence tier first.",
  row_incomplete: "This track is missing owner details. Contact support.",
  embed_failed:
    "We couldn't add this track to search, so nothing was published. Try again.",
  // The flip itself failed and rolled back. Nothing a seller can act on, so it names the door out.
  publish_failed: "Something went wrong. Contact support.",
  publish_timeout:
    "This track has too many versions to publish in one go. Contact support.",
  has_deals:
    "A version of this track is part of a licence deal and can't be deleted.",
  attach_inconsistent: "Something went wrong. Contact support.",
  unknown: "Something went wrong. Contact support.",
};

/** The one rule that reads differently depending on which write hit it. */
const PUBLISH_REFUSAL_MESSAGES: Partial<Record<TrackFamilyReason, string>> = {
  not_a_draft: "This track is already live.",
};

/** The sentence for a reason, as the operation it came out of words it. */
export function refusalMessage(
  reason: string,
  operation?: TrackFamilyOperation
): string {
  const known = reason in REFUSAL_MESSAGES ? (reason as TrackFamilyReason) : "unknown";
  if (operation === "publish") {
    const override = PUBLISH_REFUSAL_MESSAGES[known];
    if (override) return override;
  }
  return REFUSAL_MESSAGES[known];
}

/** A rule the server refused on — the only error shape any caller in this module throws. */
export class TrackFamilyRefusal extends Error {
  readonly reason: TrackFamilyReason;
  readonly status: number;

  constructor(reason: TrackFamilyReason, message: string, status: number) {
    super(message);
    this.name = "TrackFamilyRefusal";
    this.reason = reason;
    this.status = status;
  }
}

/** The `{reason, message}` FastAPI wraps in `detail`. */
interface RefusalBody {
  detail?: { reason?: string; message?: string };
}

/**
 * The rule an HTTP failure was refused by.
 *
 * A response that carries no structured detail — a proxy error, a gateway timeout — becomes
 * `unknown` rather than leaking an HTTP sentence into the sheet. Exported because `delete_tracks`
 * lives on another router but is refused by these same rules.
 */
export function familyRefusalOf(
  error: ApiError,
  operation?: TrackFamilyOperation
): TrackFamilyRefusal {
  const detail = (error.body as RefusalBody | undefined)?.detail;
  const reason = detail?.reason;
  const known =
    reason && reason in REFUSAL_MESSAGES ? (reason as TrackFamilyReason) : "unknown";
  return new TrackFamilyRefusal(
    known,
    detail?.message || refusalMessage(known, operation),
    error.status
  );
}

/**
 * Every call in this module goes through here, so a caller only ever catches one type and can
 * always branch on `reason`.
 */
async function refusable<T>(
  operation: TrackFamilyOperation,
  run: () => Promise<T>
): Promise<T> {
  try {
    return await run();
  } catch (error) {
    if (error instanceof ApiError) throw familyRefusalOf(error, operation);
    console.error(`[TrackFamily] ${operation} request failed:`, error);
    throw new TrackFamilyRefusal("unknown", REFUSAL_MESSAGES.unknown, 0);
  }
}

// ─────────────────────────────────────────────────────────────────────────────
// Wire shapes — pinned in `api/track_family_routes.py`
// ─────────────────────────────────────────────────────────────────────────────

/** Every count a confirmation sentence needs. A version id resolves to its main. */
export interface TrackFamilyPreview {
  main_id: string;
  is_main: boolean;
  title: string | null;
  draft_versions: number;
  live_versions: number;
  unanalysed_versions: number;
  live_without_search: number;
  parent_tier: string | null;
  parent_analysis_status: string | null;
  in_playlists: number;
  in_storefronts: number;
}

export interface AttachVersionResult {
  main_id: string;
  attached_ids: string[];
  /** How many of the source's own versions came along. */
  brought_along: number;
}

export interface DetachVersionResult {
  /** The full row, so the store can put it back in the list without a second read. */
  track: Tables<"tracks">;
}

/** Why a track went live without a search entry. */
export type SkippedSearchReason =
  | "not_analysed"
  | "not_analysed_old_entry_kept"
  | "embed_failed"
  | "late_attach";

export interface PublishFamilyResult {
  main_id: string;
  published_ids: string[];
  skipped_search: Array<{ id: string; why: SkippedSearchReason }>;
  error: { reason: string; message: string } | null;
}

export interface MoveFamilyToDraftsResult {
  main_id: string;
  moved_ids: string[];
  /** Without a tier the family cannot go live again, and the confirm says so before the click. */
  parent_has_tier: boolean;
}

/** The family `trackId` belongs to. A version id answers for its main. */
export function getFamily(trackId: string): Promise<TrackFamilyPreview> {
  return refusable("preview", () =>
    apiClient.get<TrackFamilyPreview>(
      `/api/track-families/${encodeURIComponent(trackId)}`
    )
  );
}

/** Make `versionId` (and its own draft versions) versions of `mainId`. */
export function attachVersion(input: {
  mainId: string;
  versionId: string;
  versionType: string;
  editType: string | null;
  /** The count the seller confirmed. The write refuses if the family moved under them. */
  expectedVersions: number;
}): Promise<AttachVersionResult> {
  return refusable("attach", () =>
    apiClient.post<AttachVersionResult>(
      `/api/track-families/${encodeURIComponent(input.mainId)}/attach`,
      {
        version_id: input.versionId,
        version_type: input.versionType,
        edit_type: input.editType,
        expected_versions: input.expectedVersions,
      }
    )
  );
}

/** Make a version a main track again. Its type and length are kept (decision 5). */
export function detachVersion(versionId: string): Promise<DetachVersionResult> {
  return refusable("detach", () =>
    apiClient.post<DetachVersionResult>("/api/track-families/detach", {
      version_id: versionId,
    })
  );
}

/**
 * Publish each named main together with its versions. Families are independent: one refusal is
 * reported against its own main, never for the batch, so this never throws for a rule — only for
 * a request that did not arrive. Callers chunk at `MAX_MAINS_PER_PUBLISH`.
 */
export function publishFamilies(
  mainIds: string[]
): Promise<{ results: PublishFamilyResult[] }> {
  return refusable("publish", () =>
    apiClient.post<{ results: PublishFamilyResult[] }>(
      "/api/track-families/publish",
      { main_ids: mainIds }
    )
  );
}

/**
 * Unpublish a main track and every live version of it. Accepts a main that is already a draft —
 * that is the shape of the 38 published-under-draft rows, and this is how a seller clears them.
 */
export function moveFamilyToDrafts(
  trackId: string,
  expectedVersions: number
): Promise<MoveFamilyToDraftsResult> {
  return refusable("move_to_drafts", () =>
    apiClient.post<MoveFamilyToDraftsResult>(
      `/api/track-families/${encodeURIComponent(trackId)}/move-to-drafts`,
      { expected_versions: expectedVersions }
    )
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// Type validation — the UI mirror of `normalise_version_type`
// ─────────────────────────────────────────────────────────────────────────────

/** Trim, drop control characters, collapse inner whitespace — the server's `_collapse`. */
function collapse(raw: string): string {
  // eslint-disable-next-line no-control-regex
  return raw.replace(/[\u0000-\u001f\u007f]/g, " ").trim().replace(/\s+/g, " ");
}

/**
 * What the server would say about this type, said before the round trip.
 *
 * The server still decides — this only spares the seller a request to be told they left the field
 * empty, and it is the same three sentences either way.
 */
export function validateVersionType(
  raw: string
): { reason: TrackFamilyReason; message: string } | null {
  const text = collapse(raw);
  if (!text) {
    return { reason: "type_required", message: REFUSAL_MESSAGES.type_required };
  }
  if (text.length > MAX_VERSION_TYPE_LENGTH) {
    return { reason: "type_too_long", message: REFUSAL_MESSAGES.type_too_long };
  }
  if (RESERVED_VERSION_TYPES.has(text.toLowerCase())) {
    return { reason: "type_reserved", message: REFUSAL_MESSAGES.type_reserved };
  }
  return null;
}

// ─────────────────────────────────────────────────────────────────────────────
// The picker's candidate set
// ─────────────────────────────────────────────────────────────────────────────

/** A draft main track the seller can attach. Carries the two columns the attach form prefills. */
export interface DraftMainCandidate {
  id: string;
  title: string | null;
  artist: string | null;
  version_type: string | null;
  edit_type: string | null;
}

/** Only the columns the picker renders or prefills from. */
const CANDIDATE_COLUMNS = "id, title, artist, version_type, edit_type";

type CandidateRow = {
  id?: string | null;
  title?: string | null;
  artist?: string | null;
  version_type?: string | null;
  edit_type?: string | null;
};

async function resolveLabelTag(): Promise<string | null> {
  const user = await UserCacheService.getUser();
  if (!user?.id) return null;
  return LabelCacheService.getLabelTag(user.id);
}

/**
 * The label's draft MAIN tracks, ranked when there is a term and newest-first when there is not.
 *
 * The filters mirror `filterDrafts` in `drafts.ts` — the same candidate set as the drafts list,
 * which is what decision 1 allows to be attached. They are written out rather than shared because
 * importing that module here would close a cycle: the drafts store imports the writes above.
 *
 * `search_drafts_ranked` takes `p_label_tag` from its caller and is SECURITY INVOKER (DEF-043), so
 * nothing here is a permission check — the attach endpoint re-reads the label on both rows.
 */
export async function searchDraftMains(
  term: string,
  excludeId: string
): Promise<DraftMainCandidate[]> {
  const labelTag = await resolveLabelTag();
  // No label, no drafts to attach — the same answer the drafts list gives.
  if (!labelTag) return [];

  const search = toOrFilterSearchTerm(term);
  // One row of headroom: excluding the track being edited must not shorten the list below the cap.
  const limit = PICKER_RESULT_LIMIT + 1;

  let rows: CandidateRow[];
  if (search) {
    const { data, error } = await supabase.rpc("search_drafts_ranked", {
      p_label_tag: labelTag,
      p_search: search,
      p_limit: limit,
      p_offset: 0,
    });
    if (error) throw error;
    rows = (data ?? []) as CandidateRow[];
  } else {
    const { data, error } = await supabase
      .from("tracks")
      .select(CANDIDATE_COLUMNS)
      .eq("label_tag", labelTag)
      .eq("is_published", false)
      .or("origin.is.null,origin.neq.license_request")
      .is("parent_track_id", null)
      .order("updated_at", { ascending: false })
      .order("id", { ascending: false })
      .limit(limit);
    if (error) throw error;
    rows = (data ?? []) as CandidateRow[];
  }

  return rows
    .filter((row): row is CandidateRow & { id: string } =>
      Boolean(row.id) && row.id !== excludeId
    )
    .slice(0, PICKER_RESULT_LIMIT)
    .map((row) => ({
      id: row.id,
      title: row.title ?? null,
      artist: row.artist ?? null,
      version_type: row.version_type ?? null,
      edit_type: row.edit_type ?? null,
    }));
}
